import { CreditCardIcon } from '@heroicons/react/solid'
import { Field, Formik, FormikProps } from 'formik'
import numeral from 'numeral'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import StripeCheckout from 'react-stripe-checkout'
import { useUser } from 'src/common/hooks/useUser'
import useJoinEventWizard from 'src/hooks/useJoinEvent'
import { useCheckoutRequestMutation } from 'src/services/mainAPI'
import {
    updateCartIsApproved,
    updateCartPaymentMethod,
    updateCartToken,
    updateOrderId
} from 'src/store/joinevent'
import * as Yup from 'yup'
import { PayPalButton } from 'react-paypal-button-v2'
import { Spinner } from '../../Spinner'

type BillingSectionProps = {
    shippingFormRef: React.MutableRefObject<FormikProps<any>>
    voucherFormRef: React.MutableRefObject<FormikProps<any>>
    // eslint-disable-next-line no-unused-vars
    onAddVoucher: (code: string) => void
    predefinedGiftCard: any
    setCheckoutComplete: (bool: boolean) => void
}

const VoucherSchema = Yup.object().shape({
    voucher: Yup.string()
})

export const BillingSection = ({
    voucherFormRef,
    shippingFormRef,
    onAddVoucher,
    predefinedGiftCard,
    setCheckoutComplete = () => null
}: BillingSectionProps) => {
    const user = useUser()
    const [isCheckoutDisabled, setIsCheckoutDisabled] = useState(false)
    const {
        showBilling,
        isAdmin,
        isNewPurchase,
        shippableBundleProducts,
        allowVouchers,
        calculatedCart,
        isRefund
    } = useJoinEventWizard()
    const [checkout, { isLoading: checkoutLoading }] = useCheckoutRequestMutation()
    const dispatch = useDispatch()
    const showPlaceOrder = (!showBilling && !isRefund) || isAdmin

    const _handleCheckout = async () => {
        try {
            setIsCheckoutDisabled(true)

            if (shippableBundleProducts.length) {
                await shippingFormRef.current.submitForm()
            }

            // eslint-disable-next-line @typescript-eslint/await-thenable
            await checkout(null)
                .unwrap()
                .then((payload) => console.log('fulfilled', payload))
                .catch((error) => {
                    throw new Error(error.message)
                })

            // await new Promise((res) => setTimeout(() => res(null), 5000))
            if (shippableBundleProducts.length) {
                shippingFormRef?.current?.setSubmitting(false)
            }

            setIsCheckoutDisabled(false)
            setCheckoutComplete(true)
        } catch (error) {
            console.log({ error })
            alert(error.message)
            setIsCheckoutDisabled(false)
            if (shippableBundleProducts.length) {
                shippingFormRef?.current?.setSubmitting(false)
            }
        }
    }

    const onStripeToken = (token) => {
        dispatch(updateCartToken(token.id))
        dispatch(updateCartPaymentMethod(1))
        dispatch(updateCartIsApproved(false))
        void _handleCheckout()
    }

    return (
        <div className="w-full h-auto lg:h-72 shadow-md rounded-md p-8 border border-gray-200 bg-white mb-6">
            <div className="flex items-center">
                <div className="w-10 h-10 border-2 border-yf-green rounded-full flex items-center justify-center font-medium mr-5 text-lg">
                    {shippableBundleProducts.length ? 3 : 2}
                </div>
                <p className="uppercase text-yf-green font-medium text-xl md:text-2xl">
                    Payment Options
                </p>
            </div>
            <div className="h-px bg-gray-300 my-4"></div>
            <div className="block lg:float-right float-none md:pb-5 mr-5 mb-5">
                <Formik
                    innerRef={voucherFormRef}
                    validateOnChange={true}
                    validateOnMount={false}
                    initialValues={{
                        voucher: ''
                    }}
                    validationSchema={VoucherSchema}
                    onSubmit={(values, { resetForm }) => {
                        onAddVoucher(values.voucher)
                        resetForm()
                    }}
                >
                    {({ isSubmitting, submitForm, setFieldValue }) => (
                        <Field name="voucher">
                            {({ field, meta }) => (
                                <>
                                    {predefinedGiftCard && showBilling && (
                                        <div
                                            className="mb-2 flex relative"
                                            onClick={() => {
                                                setFieldValue(
                                                    'voucher',
                                                    predefinedGiftCard.code
                                                )
                                                void submitForm()
                                            }}
                                        >
                                            <button
                                                disabled={false}
                                                className="flex items-center justify-center text-base font-medium block w-full rounded-md py-3 bg-gradient-to-r from-yf-purple-500 to-yf-purple-700"
                                            >
                                                <span className="flex items-center justify-center mx-2 space-x-3">
                                                    <p className="flex-grow text-white">
                                                        UP TO{' '}
                                                        {numeral(
                                                            predefinedGiftCard.balance
                                                        ).format('$0[.]00')}{' '}
                                                        OFF
                                                    </p>
                                                    <CreditCardIcon className="flex-none w-6 h-6 mr-2 text-white" />
                                                </span>
                                            </button>
                                            <span className="flex absolute -top-1 -right-1 h-3 w-3">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yf-green-200 opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-3 w-3 bg-yf-green-400"></span>
                                            </span>
                                        </div>
                                    )}

                                    {allowVouchers && showBilling ? (
                                        <div className="inline-block w-full">
                                            <input
                                                disabled={isSubmitting}
                                                type="text"
                                                placeholder="Add Promo Code"
                                                className="mt-1 mb-2 block w-full rounded-md py-3 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400 disabled:bg-gray-300 disabled:opacity-50"
                                                {...field}
                                            />
                                            <button
                                                className="text-yf-green float-right font-medium border-none"
                                                onClick={submitForm}
                                            >
                                                Apply Code
                                            </button>
                                            {meta.touched && meta.error && (
                                                <span className="text-red-500 text-sm">
                                                    {meta.error}
                                                </span>
                                            )}
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </Field>
                    )}
                </Formik>
            </div>

            {showBilling && (
                <>
                    <div className="block mb-2">
                        <StripeCheckout
                            token={onStripeToken}
                            email={user?.email}
                            zipCode={true}
                            stripeKey={process.env.NEXT_PUBLIC_STRIPE_API_KEY}
                            label="Credit/Debit Card"
                        />
                    </div>

                    <div className="block mb-2 max-w-[225px]">
                        <PayPalButton
                            onError={(e) => console.log(e)}
                            amount={calculatedCart.total}
                            onApprove={(actions) => {
                                dispatch(updateCartPaymentMethod(2))
                                dispatch(updateOrderId(actions.orderID))
                                void _handleCheckout()
                            }}
                            options={{
                                intent: 'authorize',
                                clientId: process.env.NEXT_PUBLIC_PAYPAL_CLIENT,
                                disableFunding: 'credit,card'
                            }}
                        />
                    </div>
                </>
            )}

            {showPlaceOrder ? (
                <>
                    <button
                        className="bg-yf-green rounded py-4 px-10 flex items-center text-white font-medium text-sm min-w-[225px] justify-center disabled:opacity-50"
                        onClick={_handleCheckout}
                        disabled={checkoutLoading || isCheckoutDisabled}
                    >
                        {checkoutLoading ? (
                            <>
                                <Spinner size={24} />
                                Loading...
                            </>
                        ) : (
                            <p>{isNewPurchase ? 'Place Order' : 'Update Order'}</p>
                        )}
                    </button>
                </>
            ) : null}
            {isRefund && (
                <span className="text-red-500 text-sm">
                    Please contact support in order to receive a refund
                </span>
            )}
        </div>
    )
}
